import { StringHelper } from "./stringHelper";
import { UserAgentHelper } from "./userAgentHelper";

class Font {
    public size: number = 14;
    public family: string = "sans-serif";
    public weight: string = "normal";
    public style: string = "normal";
}

interface Box {
    width: number;
    height: number;
}

interface Rect {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface RectsInfo {
    rects: Array<Rect>;
}

const _onSafari = UserAgentHelper.getBrowser() === "safari";

export default class MeasureTextSizeService {
    private svg?: SVGSVGElement;

    public constructor(private el?: SVGTextElement) {}

    public measureLine(
        line : string,
        font = new Font()
    ): RectsInfo {
        if (!this.svg || !this.el) {
            throw new Error("textElement is not initialized!");
        }

        this.el.textContent = "";
        this.el.style.whiteSpace = "pre";
        this.el.style.fontSize = String(font.size);
        this.el.style.fontFamily = font.family;
        this.el.style.fontWeight = font.weight;
        this.el.style.fontStyle = font.style;
        // Firefox用
        this.el.setAttributeNS(null, "font-size", String(font.size));
        
        this.el.setAttributeNS("http://www.w3.org/2000/svg", "writing-mode", "");
        this.el.style.writingMode = "";
        this.el.style.webkitWritingMode = "";

        let tspans : Array<SVGTSpanElement> = [];
        for(let i = 0; i < line.length; ++i) {
            let word = line[i];
            let tspan = document.createElementNS(
                "http://www.w3.org/2000/svg",
                "tspan"
            );
            tspan.textContent = word;
            this.el.appendChild(tspan);
            tspans.push(tspan);
        }
        // Chromeでは、appendChildしてからstyle.writingModeを設定すると描画できないエラーになるので、いちいちappendChild()する
        this.el.remove();
        this.svg.appendChild(this.el);

        let rects : Array<Rect> = [];
        for(let i = 0; i < line.length; ++i) {
            let bounds = tspans[i].getBBox();
            let width = bounds.width;
            let height = (2 * Math.abs(bounds.y) - bounds.height) * 1.2;
            if (width <= 0) width = font.size * 1.1;
            if (height <= 0) height = font.size * 1.1;
            rects.push({x: bounds.x, y: bounds.y, width: width, height: height});
        }
        for(let i = 0; i < tspans.length; ++i) {
            this.el.removeChild(tspans[i]);
            delete tspans[i];
        }
        for(let i = 0; i < line.length; ++i) {
            this.el.textContent = line[i];
            let bounds = this.el.getBBox();
            rects[i].x -= bounds.x;
        }        
        
        return { "rects": rects };
    }

    public measure(
        text: string,
        font: Font = new Font(),
        landscape: boolean = false
    ): Box {
        if (!this.svg || !this.el) {
            throw new Error("textElement is not initialized!");
        }

        let width = 0;
        let height = 0;

        this.el.textContent = text;
        this.el.style.whiteSpace = "pre";
        this.el.style.fontSize = String(font.size);
        this.el.style.fontFamily = font.family;
        this.el.style.fontWeight = font.weight;
        this.el.style.fontStyle = font.style;
        // Firefox用
        this.el.setAttributeNS(null, "font-size", String(font.size));
        if (landscape) {
            this.el.setAttributeNS("http://www.w3.org/2000/svg", "writing-mode", "tb");
            this.el.style.writingMode = "vertical-rl";
            this.el.style.webkitWritingMode = "vertical-rl";
        } else {
            this.el.setAttributeNS("http://www.w3.org/2000/svg", "writing-mode", "");
            this.el.style.writingMode = "";
            this.el.style.webkitWritingMode = "";
        }

        // サイズ計測
        // Chromeでは、appendChildしてからstyle.writingModeを設定すると描画できないエラーになるので、いちいちappendChild()する
        this.svg.appendChild(this.el);
        let bounds = this.el.getBBox();
        this.el.remove();
        if (_onSafari && landscape && !StringHelper.isHankaku(text)) {
            width = bounds.height;
            height = bounds.width;
        } else {
            width = bounds.width;
            height = landscape
                ? bounds.height
                : (2 * Math.abs(bounds.y) - bounds.height) * 1.2;
        }
        // フォントによってはうまく計算されないことがあるので、
        // 計算がおかしい場合はフォントサイズの1.1倍として返す
        if (width <= 0) width = font.size * 1.1;
        if (height <= 0) height = font.size * 1.1;

        return { width, height };
    }

    public prepare(): void {
        this.svg = this.createCanvas();
        this.el = this.createTextElement();
        //this.svg.appendChild(this.el);
        document.body.appendChild(this.svg);
    }

    public destroy(): void {
        if (this.el) {
            //this.el.remove();
            delete this.el;
        }
        if (this.svg) {
            this.svg.remove();
            delete this.svg;
        }
    }

    // public static dispose(fn: (service: MeasureTextSizeService) => void): void {
    //     const service = new MeasureTextSizeService();
    //     service.prepare();
    //     fn(service);
    //     service.destroy();
    // }

    private createCanvas(): SVGSVGElement {
        const el = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "svg"
        );
        el.style.position = "fixed";
        // display: none が使えなかったので画面の外に出す
        el.style.left = "-1px";
        el.style.top = "-1px";
        el.style.width = "1px";
        el.style.height = "1px";
        return el;
    }

    private createTextElement(): SVGTextElement {
        const el = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "text"
        );
        el.setAttributeNS("http://www.w3.org/2000/svg", "x", "0");
        el.setAttributeNS("http://www.w3.org/2000/svg", "y", "0");
        return el;
    }
}
